import type { Terminal } from "xterm";
import { api, ApiError } from "@/api/client";

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);

/** Browser images must reach the PTY host as files, not a remote Ctrl+V.
 * Only a pasted image is intercepted; text remains xterm's native paste.
 */
export function terminalClipboard(
  container: HTMLElement,
  terminal: Terminal,
  sessionId: string,
  connected: () => boolean,
  state: (uploading: boolean, error?: unknown) => void,
) {
  let disposed = false;
  let pending: AbortController | null = null;
  let queue = Promise.resolve();

  async function upload(images: Blob[]) {
    for (const image of images) {
      if (disposed) return;
      if (!connected()) throw new ApiError(409, "clipboard.disconnected");
      if (!image.size || image.size > MAX_IMAGE_BYTES)
        throw new ApiError(413, "clipboard.too_large");
      if (!IMAGE_TYPES.has(image.type)) throw new ApiError(415, "clipboard.invalid_image");
      state(true);
      const bytes = new Uint8Array(await image.arrayBuffer());
      const chunks: string[] = [];
      for (let offset = 0; offset < bytes.length; offset += 32768)
        chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + 32768)));
      if (disposed) return;
      const controller = new AbortController();
      pending = controller;
      const result = await api.sessions.uploadImage(
        sessionId,
        {
          mime_type: image.type,
          data_b64: btoa(chunks.join("")),
        },
        controller.signal,
      );
      pending = null;
      if (disposed) return;
      if (!connected()) throw new ApiError(409, "clipboard.disconnected");
      // The agent generates this /tmp path. Do not submit the prompt/Enter.
      terminal.paste(result.path);
      state(false);
    }
  }

  function enqueue(images: Blob[]) {
    queue = queue
      .then(() => upload(images))
      .catch((error) => {
        pending = null;
        if (!disposed) state(false, error);
      });
    return queue;
  }

  function onPaste(event: ClipboardEvent) {
    const images = [...(event.clipboardData?.items ?? [])]
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    if (!images.length)
      images.push(
        ...[...(event.clipboardData?.files ?? [])].filter((file) => file.type.startsWith("image/")),
      );
    if (!images.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void enqueue(images);
  }

  function isPasteShortcut(event: KeyboardEvent) {
    return (
      (event.ctrlKey || event.metaKey) &&
      !event.shiftKey &&
      !event.altKey &&
      (event.code === "KeyV" || event.keyCode === 86 || event.key.toLowerCase() === "v")
    );
  }

  function onPasteKey(event: KeyboardEvent) {
    if (!isPasteShortcut(event)) return;
    // Stop xterm before it turns the physical V key into remote ^V, including
    // when event.key is Cyrillic "м". Leave the browser's default paste intact.
    event.stopImmediatePropagation();
  }

  terminal.attachCustomKeyEventHandler(
    (event) => event.type === "keyup" || !isPasteShortcut(event),
  );
  container.addEventListener("keydown", onPasteKey, true);
  container.addEventListener("keypress", onPasteKey, true);
  container.addEventListener("paste", onPaste, true);

  return {
    async readImages(): Promise<boolean> {
      if (typeof navigator.clipboard?.read !== "function") return false;
      const items = await navigator.clipboard.read();
      const images: Blob[] = [];
      for (const item of items) {
        const type = item.types.find((value) => IMAGE_TYPES.has(value));
        if (type) images.push(await item.getType(type));
      }
      if (!images.length) return false;
      await enqueue(images);
      return true;
    },
    dispose() {
      disposed = true;
      pending?.abort();
      container.removeEventListener("keydown", onPasteKey, true);
      container.removeEventListener("keypress", onPasteKey, true);
      container.removeEventListener("paste", onPaste, true);
    },
  };
}
